import { ConversationItem } from '@vivport/backend/types';
import { invalidateDriveMirroredRefs } from '@/hooks/useDrivesQueries';
import { invalidateMailboxImportedRefs } from '@/hooks/useMemberExternalAccountQueries';
import { queryClient } from '@/lib/query-client';
import { useAsyncStore } from '@/store/asyncStore';
import {
  type EphemeralToolCall,
  type EphemeralToolCallStatus,
} from './EphemeralThinkingSurface';

export function isoToLocalInput(iso: string, isAllDay: boolean): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return isAllDay ? date : `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ASYNC_TRACKABLE_CONTENT_TYPES = ['ANCHOR', 'DOCUMENT'] as const;
type AsyncTrackableContentType = (typeof ASYNC_TRACKABLE_CONTENT_TYPES)[number];

export function buildDetailsPayload(contentType: AsyncTrackableContentType, contentId: number) {
  if (contentType === 'ANCHOR') {
    return { anchorId: contentId };
  }
  return { documentId: contentId };
}

export function mapToolKeyToAsyncContentType(toolKey: string): AsyncTrackableContentType | null {
  if (toolKey === 'content.createAnchor') {
    return 'ANCHOR';
  }
  if (toolKey === 'content.createDocument') {
    return 'DOCUMENT';
  }
  return null;
}

export function extractCreatedContentId(output: unknown): number | null {
  if (!output || typeof output !== 'object') {
    return null;
  }
  const id = (output as { id?: unknown }).id;
  if (typeof id === 'number' && Number.isInteger(id) && id > 0) {
    return id;
  }
  const nestedDocumentId = (output as { document?: { id?: unknown } }).document?.id;
  return typeof nestedDocumentId === 'number' && Number.isInteger(nestedDocumentId) && nestedDocumentId > 0
    ? nestedDocumentId
    : null;
}

function refreshMailboxKeptStateFromConversationItem(item: ConversationItem): void {
  if (item.kind !== 'tool_result' || item.status !== 'ok' || item.toolKey !== 'mailbox.import') return;
  const account =
    item.output && typeof item.output === 'object'
      ? (item.output as { account?: { id?: unknown } }).account
      : undefined;
  const memberExternalAccountId = account && typeof account.id === 'number' ? account.id : undefined;
  void invalidateMailboxImportedRefs(queryClient, memberExternalAccountId);
}

function refreshDriveKeptStateFromConversationItem(item: ConversationItem): void {
  if (item.kind !== 'tool_result' || item.status !== 'ok' || item.toolKey !== 'drives.keep') return;
  const driveId =
    item.output && typeof item.output === 'object' ? (item.output as { driveId?: unknown }).driveId : undefined;
  void invalidateDriveMirroredRefs(queryClient, typeof driveId === 'number' ? driveId : undefined);
}

export function registerContentTaskFromConversationItem(item: ConversationItem): void {
  refreshMailboxKeptStateFromConversationItem(item);
  refreshDriveKeptStateFromConversationItem(item);
  if (item.kind !== 'tool_result' || item.status !== 'ok') return;
  const contentType = mapToolKeyToAsyncContentType(item.toolKey);
  if (!contentType) return;
  const contentId = extractCreatedContentId(item.output);
  if (!contentId) return;
  const asyncState = useAsyncStore.getState();
  const alreadyTracked = asyncState.tasks.some(
    (t) => t.contentType === contentType && Number(t.contentId) === contentId
  );
  if (alreadyTracked) return;
  asyncState.addTask({
    title: `Processing ${contentType}`,
    description: `${contentType[0]}${contentType.slice(1).toLowerCase()} processing started.`,
    status: 'processing',
    contentType,
    contentId,
    payload: buildDetailsPayload(contentType, contentId),
  });
}

export type EphemeralState =
  | { phase: 'hidden' }
  | { phase: 'streaming'; streamingText: string; toolCalls: EphemeralToolCall[] }
  | { phase: 'cancelled'; toolCalls: EphemeralToolCall[] };

export type EphemeralAction =
  | { type: 'START' }
  | { type: 'NEW_ITERATION' }
  | { type: 'TEXT_DELTA'; text: string }
  | { type: 'TOOL_START'; toolCallId: string; name: string }
  | { type: 'TOOL_AWAITING'; toolCallId: string }
  | { type: 'TOOL_RUNNING'; toolCallId: string }
  | { type: 'TOOL_DONE'; toolCallId: string; status: 'completed' | 'failed' | 'pending_approval' }
  | { type: 'CANCEL' }
  | { type: 'HIDE' };

export function updateToolStatus(
  state: Extract<EphemeralState, { phase: 'streaming' }>,
  toolCallId: string,
  status: EphemeralToolCallStatus,
): EphemeralState {
  return {
    ...state,
    toolCalls: state.toolCalls.map((tc) =>
      tc.toolCallId === toolCallId ? { ...tc, status } : tc
    ),
  };
}

export function ephemeralReducer(state: EphemeralState, action: EphemeralAction): EphemeralState {
  if (action.type === 'START') {
    return { phase: 'streaming', streamingText: '', toolCalls: [] };
  }
  if (action.type === 'HIDE') {
    return { phase: 'hidden' };
  }
  if (action.type === 'CANCEL') {
    const toolCalls = state.phase === 'streaming' ? state.toolCalls : state.phase === 'cancelled' ? state.toolCalls : [];
    return { phase: 'cancelled', toolCalls };
  }
  if (action.type === 'NEW_ITERATION') {
    if (state.phase === 'streaming') {
      return { phase: 'streaming', streamingText: '', toolCalls: [] };
    }
    return state;
  }
  if (state.phase !== 'streaming') return state;
  if (action.type === 'TEXT_DELTA') {
    return { ...state, streamingText: state.streamingText + action.text };
  }
  if (action.type === 'TOOL_START') {
    if (state.toolCalls.some((tc) => tc.toolCallId === action.toolCallId)) return state;
    return {
      ...state,
      toolCalls: [...state.toolCalls, { toolCallId: action.toolCallId, name: action.name, status: 'invoking' }],
    };
  }
  if (action.type === 'TOOL_AWAITING') {
    return updateToolStatus(state, action.toolCallId, 'awaiting_execution');
  }
  if (action.type === 'TOOL_RUNNING') {
    return updateToolStatus(state, action.toolCallId, 'running');
  }
  if (action.type === 'TOOL_DONE') {
    return updateToolStatus(state, action.toolCallId, action.status);
  }
  return state;
}
